import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowDownIcon, ArrowUpIcon, BriefcaseIcon, BuildingIcon, CheckIcon, ClipboardListIcon, ContactIcon, PackageIcon, PieChartIcon, PlusIcon, RotateCcwIcon, SearchIcon, SlidersHorizontalIcon, Trash2Icon, TrendingUpIcon, UserRoundCogIcon, UsersIcon, XIcon } from 'lucide-react';
import type { NavigationGroup, NavigationItem, NavigationProfile } from './navigationTypes';
type NavigationSettingsPanelProps = {
  isOpen: boolean;
  groups: NavigationGroup[];
  profiles: NavigationProfile[];
  selectedProfileId: string;
  compactRail: boolean;
  keepPinned: boolean;
  onProfileChange: (profileId: string) => void;
  onClose: () => void;
  onSave: () => void;
  onReset: () => void;
  onToggleItem: (groupId: string, itemId: string) => void;
  onRenameItem: (groupId: string, itemId: string, label: string) => void;
  onMoveItem: (groupId: string, itemId: string, direction: 'up' | 'down') => void;
  onAddItem: (groupId: string, label: string, icon: NavigationItem['icon']) => void;
  onRemoveItem: (groupId: string, itemId: string) => void;
  onCompactRailChange: (value: boolean) => void;
  onKeepPinnedChange: (value: boolean) => void;
};
const iconOptions = [{
  id: 'briefcase',
  label: 'Briefcase',
  icon: BriefcaseIcon
}, {
  id: 'people',
  label: 'People',
  icon: UsersIcon
}, {
  id: 'contact',
  label: 'Contact',
  icon: ContactIcon
}, {
  id: 'building',
  label: 'Building',
  icon: BuildingIcon
}, {
  id: 'cases',
  label: 'Cases',
  icon: ClipboardListIcon
}, {
  id: 'pipeline',
  label: 'Pipeline',
  icon: TrendingUpIcon
}, {
  id: 'package',
  label: 'Package',
  icon: PackageIcon
}, {
  id: 'report',
  label: 'Report',
  icon: PieChartIcon
}];
type CatalogItem = {
  label: string;
  iconId: string;
  icon: NavigationItem['icon'];
};
const catalogByGroup: Record<string, CatalogItem[]> = {
  objects: [{
    label: 'Applications',
    iconId: 'briefcase',
    icon: BriefcaseIcon
  }, {
    label: 'Opportunities',
    iconId: 'pipeline',
    icon: TrendingUpIcon
  }, {
    label: 'Tasks',
    iconId: 'cases',
    icon: ClipboardListIcon
  }, {
    label: 'Documents',
    iconId: 'contact',
    icon: ContactIcon
  }, {
    label: 'Properties',
    iconId: 'building',
    icon: BuildingIcon
  }],
  components: [{
    label: 'Task Queue',
    iconId: 'cases',
    icon: ClipboardListIcon
  }, {
    label: 'Document Center',
    iconId: 'contact',
    icon: ContactIcon
  }, {
    label: 'Rate Lock Desk',
    iconId: 'pipeline',
    icon: TrendingUpIcon
  }, {
    label: 'Compliance Review',
    iconId: 'report',
    icon: PieChartIcon
  }, {
    label: 'Funding Calendar',
    iconId: 'package',
    icon: PackageIcon
  }]
};
export function NavigationSettingsPanel({
  isOpen,
  groups,
  profiles,
  selectedProfileId,
  compactRail,
  keepPinned,
  onProfileChange,
  onClose,
  onSave,
  onReset,
  onToggleItem,
  onRenameItem,
  onMoveItem,
  onAddItem,
  onRemoveItem,
  onCompactRailChange,
  onKeepPinnedChange
}: NavigationSettingsPanelProps) {
  const [addingToGroup, setAddingToGroup] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [selectedIconId, setSelectedIconId] = useState(iconOptions[0].id);
  const [removeConfirmation, setRemoveConfirmation] = useState<{
    groupId: string;
    itemId: string;
    label: string;
  } | null>(null);
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (removeConfirmation) setRemoveConfirmation(null);else if (addingToGroup) setAddingToGroup(null);else onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, addingToGroup, removeConfirmation]);
  const startAdding = (groupId: string) => {
    setAddingToGroup(groupId);
    setNewLabel('');
    setSelectedIconId(groupId === 'components' ? 'pipeline' : 'briefcase');
    setRemoveConfirmation(null);
  };
  const addItem = (groupId: string) => {
    const label = newLabel.trim();
    const icon = iconOptions.find(option => option.id === selectedIconId)?.icon;
    if (!label || !icon) return;
    onAddItem(groupId, label, icon);
    setAddingToGroup(null);
    setNewLabel('');
  };
  return <AnimatePresence data-id="element-1" data-mp="3erek9lz197sp">
      {isOpen && <div className="fixed inset-0 z-[70]" data-id="element-2" data-mp="3eqd5p7it6368">
          <motion.button type="button" aria-label="Close navigation settings" className="absolute inset-0 w-full bg-slate-950/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} data-id="element-3" data-mp="1ydeomn7804b9" />
          <motion.aside role="dialog" aria-modal="true" aria-labelledby="navigation-settings-title" className="absolute right-0 top-0 flex h-full w-full max-w-xl flex-col bg-white shadow-2xl" initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 28,
        stiffness: 310
      }} data-id="element-4" data-mp="24tojhboemwt8">
            <header className="flex items-start justify-between border-b border-slate-200 px-6 py-5" data-id="element-5" data-mp="1h2j7x8phyqoq">
              <div data-id="element-6" data-mp="tcotwntfyw2w">
                <div className="mb-2 flex items-center gap-2 text-[#C8102E]" data-id="element-7" data-mp="tc5a11k8dxm8">
                  <SlidersHorizontalIcon className="h-4 w-4" data-id="element-8" data-mp="of554tz4b0wp" />
                  <span className="text-xs font-bold uppercase tracking-[0.14em]" data-id="element-9" data-mp="2oig2nlk99ie9">
                    Navigation settings
                  </span>
                </div>
                <h2 id="navigation-settings-title" className="text-xl font-bold text-slate-900" data-id="element-10" data-mp="2y7hfj2vi7jo0">
                  Customize navigation
                </h2>
                <p className="mt-1 text-sm leading-5 text-slate-500" data-id="element-11" data-mp="25j256tnbhpj0">
                  Configure a dedicated sidebar view for each team profile.
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Close navigation settings" className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E]" data-id="element-12" data-mp="2fgzrhblm9c8z">
                <XIcon className="h-5 w-5" data-id="element-13" data-mp="3uwfc8qmtdrnt" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-14" data-mp="1j9bwbmlqlazp">
              <section aria-labelledby="profile-selector-title" className="mb-7 rounded-xl border border-slate-200 bg-slate-50 p-4" data-id="element-15" data-mp="1f8s1wqsjslh9">
                <div className="mb-3 flex items-center gap-2 text-slate-700" data-id="element-16" data-mp="1afqgdzxu5nop">
                  <UserRoundCogIcon className="h-4 w-4 text-[#C8102E]" data-id="element-17" data-mp="15iq95lp92ew0" />
                  <h3 id="profile-selector-title" className="text-sm font-bold" data-id="element-18" data-mp="10kyguqkp0qra">
                    Editing navigation for
                  </h3>
                </div>
                <label htmlFor="profile-selector" className="sr-only" data-id="element-19" data-mp="rixl5y53ycrq">
                  Select profile to edit
                </label>
                <select id="profile-selector" value={selectedProfileId} onChange={event => onProfileChange(event.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-[#C8102E] focus:ring-2 focus:ring-[#C8102E]/15" data-id="element-20" data-mp="mlfn52smphyi">
                  {profiles.map(profile => <option key={profile.id} value={profile.id} data-id="element-21" data-mp="1g2zrrtnh24u1">
                      {profile.label}
                    </option>)}
                </select>
                <p className="mt-2 text-xs leading-5 text-slate-500" data-id="element-22" data-mp="1kzzx22chz3k3">
                  Changes apply immediately to this profile’s live sidebar
                  preview.
                </p>
              </section>

              <section aria-labelledby="sidebar-preferences-title" className="mb-7" data-id="element-23" data-mp="x43z4mr9hyib">
                <h3 id="sidebar-preferences-title" className="text-sm font-bold text-slate-900" data-id="element-24" data-mp="122faj1x7gh2n">
                  Sidebar preferences
                </h3>
                <div className="mt-3 divide-y divide-slate-200 rounded-xl border border-slate-200" data-id="element-25" data-mp="2wg6scopou796">
                  <PreferenceRow id="compact-rail" label="Use compact rail" description="Reduce the collapsed sidebar width." checked={compactRail} onChange={onCompactRailChange} data-id="element-26" data-mp="kuxtup0ob0ke" />
                  <PreferenceRow id="keep-pinned" label="Keep sidebar pinned" description="Keep the navigation panel expanded." checked={keepPinned} onChange={onKeepPinnedChange} data-id="element-27" data-mp="prxziytm21az" />
                </div>
              </section>

              <div className="space-y-7" data-id="element-28" data-mp="1dg7izu3cqcyy">
                {groups.map(group => <section key={group.id} aria-labelledby={`${group.id}-settings-title`} data-id="element-29" data-mp="18k8qbuazq8sq">
                    <div className="flex items-start justify-between gap-3" data-id="element-30" data-mp="3ablqy75xduiz">
                      <div data-id="element-31" data-mp="3ofdy9y6dzpvq">
                        <h3 id={`${group.id}-settings-title`} className="text-sm font-bold text-slate-900" data-id="element-32" data-mp="3ofnqlsewm94l">
                          {group.title}
                        </h3>
                        <p className="mt-0.5 text-xs text-slate-500" data-id="element-33" data-mp="3ej183aa1vu5q">
                          Toggle, rename, rearrange, or remove items.
                        </p>
                      </div>
                      <button type="button" onClick={() => startAdding(group.id)} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#C8102E]/25 bg-white px-2.5 py-1.5 text-xs font-bold text-[#C8102E] transition-colors hover:bg-[#C8102E]/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E]" data-id="element-34" data-mp="b2c0rm2n8gf0">
                        <PlusIcon className="h-3.5 w-3.5" data-id="element-35" data-mp="p00kgqkz5hih" />
                        Add {group.id === 'objects' ? 'object' : 'component'}
                      </button>
                    </div>

                    {addingToGroup === group.id && <AddItemForm groupId={group.id} groupTitle={group.title} existingLabels={group.items.map(item => item.label)} label={newLabel} selectedIconId={selectedIconId} onLabelChange={setNewLabel} onIconChange={setSelectedIconId} onCancel={() => setAddingToGroup(null)} onAdd={() => addItem(group.id)} data-id="element-36" data-mp="11ckuu4bm32rj" />}

                    <div className="mt-3 overflow-hidden rounded-xl border border-slate-200" data-id="element-37" data-mp="1tynricl2p8pa">
                      {group.items.map((item, index) => {
                  const Icon = item.icon;
                  const confirming = removeConfirmation?.itemId === item.id && removeConfirmation.groupId === group.id;
                  return <div key={item.id} className="border-b border-slate-200 last:border-b-0" data-id="element-38" data-mp="19bxuazdq9kq7" data-mpi={index}>
                            <div className="flex items-center gap-3 px-3 py-3" data-id="element-39" data-mp="1j6pvseafkrq9">
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600" data-id="element-40" data-mp="1wynya69mzpdg">
                                <Icon className="h-4 w-4" data-id="element-41" data-mp="1s2p1bua8sz1f" />
                              </span>
                              <label className="sr-only" htmlFor={`${selectedProfileId}-${item.id}-label`} data-id="element-42" data-mp="26vsl88bb3ovb">
                                Label for {item.label}
                              </label>
                              <input id={`${selectedProfileId}-${item.id}-label`} value={item.label} onChange={event => onRenameItem(group.id, item.id, event.target.value)} className="min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-2 py-1.5 text-sm font-medium text-slate-800 outline-none focus:border-[#C8102E] focus:ring-2 focus:ring-[#C8102E]/15" data-id="element-43" data-mp="7mlum7zoasz8" />
                              <div className="flex items-center gap-1" data-id="element-44" data-mp="1vi0d5h92nuzj">
                                <button type="button" aria-label={`Move ${item.label} up`} disabled={index === 0} onClick={() => onMoveItem(group.id, item.id, 'up')} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E]" data-id="element-45" data-mp="3kxgl4tb9fanu">
                                  <ArrowUpIcon className="h-4 w-4" data-id="element-46" data-mp="16vpgwas9f2o5" />
                                </button>
                                <button type="button" aria-label={`Move ${item.label} down`} disabled={index === group.items.length - 1} onClick={() => onMoveItem(group.id, item.id, 'down')} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E]" data-id="element-47" data-mp="123o8x124jy3x">
                                  <ArrowDownIcon className="h-4 w-4" data-id="element-48" data-mp="2zu4ixct9ue9z" />
                                </button>
                                <label className="relative ml-1 inline-flex cursor-pointer items-center" data-id="element-49" data-mp="38szqotbyrwfe">
                                  <span className="sr-only" data-id="element-50" data-mp="2c2z32xnbzlzo">
                                    Show {item.label} in navigation
                                  </span>
                                  <input type="checkbox" checked={item.visible} onChange={() => onToggleItem(group.id, item.id)} className="peer sr-only" data-id="element-51" data-mp="2gzpeh7o6xter" />
                                  <span className="h-5 w-9 rounded-full bg-slate-300 transition-colors peer-checked:bg-[#C8102E] peer-focus-visible:ring-2 peer-focus-visible:ring-[#C8102E] peer-focus-visible:ring-offset-2 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:shadow-sm after:transition-transform peer-checked:after:translate-x-4" data-id="element-52" data-mp="1xbgh8j7six8e" />
                                </label>
                                <button type="button" aria-label={`Remove ${item.label}`} onClick={() => setRemoveConfirmation({
                          groupId: group.id,
                          itemId: item.id,
                          label: item.label
                        })} className="ml-1 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E]" data-id="element-53" data-mp="2hp2ut27medqt">
                                  <Trash2Icon className="h-4 w-4" data-id="element-54" data-mp="eezj1gayx4hx" />
                                </button>
                              </div>
                            </div>
                            {confirming && <div className="flex items-center justify-between gap-3 border-t border-red-100 bg-red-50 px-3 py-2.5" data-id="element-55" data-mp="s878p2fhnpwz">
                                <p className="text-xs font-medium text-red-800" data-id="element-56" data-mp="x6ikvjt9acid">
                                  Remove “{item.label}” from this profile?
                                </p>
                                <span className="flex shrink-0 gap-2" data-id="element-57" data-mp="ngpjfbmtvdru">
                                  <button type="button" onClick={() => setRemoveConfirmation(null)} className="rounded-md px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E]" data-id="element-58" data-mp="1b732wdrck4at">
                                    Cancel
                                  </button>
                                  <button type="button" onClick={() => {
                          onRemoveItem(group.id, item.id);
                          setRemoveConfirmation(null);
                        }} className="rounded-md bg-[#C8102E] px-2 py-1 text-xs font-semibold text-white hover:bg-[#A50D24] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E]" data-id="element-59" data-mp="2yabaq66xcbwn">
                                    Remove
                                  </button>
                                </span>
                              </div>}
                          </div>;
                })}
                    </div>
                  </section>)}
              </div>
            </div>

            <footer className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-6 py-4" data-id="element-60" data-mp="25ev363ukf6vv">
              <button type="button" onClick={onReset} className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-white hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E]" data-id="element-61" data-mp="2a7o6qoeas96d">
                <RotateCcwIcon className="h-4 w-4" data-id="element-62" data-mp="2t57jo33vmby2" /> Reset defaults
              </button>
              <button type="button" onClick={onSave} className="inline-flex items-center gap-2 rounded-lg bg-[#C8102E] px-4 py-2 text-sm font-semibold text-white hover:bg-[#A50D24] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E] focus-visible:ring-offset-2" data-id="element-63" data-mp="ts3cnox1v2ls">
                <CheckIcon className="h-4 w-4" data-id="element-64" data-mp="36xmr9kbuqqvv" /> Save changes
              </button>
            </footer>
          </motion.aside>
        </div>}
    </AnimatePresence>;
}
type AddItemFormProps = {
  groupId: string;
  groupTitle: string;
  existingLabels: string[];
  label: string;
  selectedIconId: string;
  onLabelChange: (value: string) => void;
  onIconChange: (value: string) => void;
  onCancel: () => void;
  onAdd: () => void;
};
function AddItemForm({
  groupId,
  groupTitle,
  existingLabels,
  label,
  selectedIconId,
  onLabelChange,
  onIconChange,
  onCancel,
  onAdd
}: AddItemFormProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const catalog = catalogByGroup[groupId] ?? [];
  const normalizedLabels = new Set(existingLabels.map(item => item.trim().toLowerCase()));
  const filteredCatalog = catalog.filter(item => item.label.toLowerCase().includes(searchTerm.trim().toLowerCase()));
  const duplicateLabel = normalizedLabels.has(label.trim().toLowerCase());
  const chooseCatalogItem = (item: CatalogItem) => {
    if (normalizedLabels.has(item.label.toLowerCase())) return;
    onLabelChange(item.label);
    onIconChange(item.iconId);
  };
  return <div className="mt-3 rounded-xl border border-[#C8102E]/25 bg-[#C8102E]/[0.03] p-4" data-id="element-65" data-mp="1j0dkonvvxgn9">
      <h4 className="text-sm font-bold text-slate-900" data-id="element-66" data-mp="2glrpxhvk7lri">Add to {groupTitle}</h4>

      <div className="mt-4" data-id="element-67" data-mp="2bn83lgd207ym">
        <h5 className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-68" data-mp="2bp1bt03bf08q">
          Choose from catalog
        </h5>
        <label htmlFor={`${groupId}-catalog-search`} className="sr-only" data-id="element-69" data-mp="26mtwc0ja121n">
          Search {groupTitle.toLowerCase()} catalog
        </label>
        <div className="relative mt-2" data-id="element-70" data-mp="v9zqortqp7q1">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-71" data-mp="vb1cnrcanemj" />
          <input id={`${groupId}-catalog-search`} value={searchTerm} onChange={event => setSearchTerm(event.target.value)} placeholder={`Search ${groupTitle.toLowerCase()} catalog`} className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-800 outline-none focus:border-[#C8102E] focus:ring-2 focus:ring-[#C8102E]/15" data-id="element-72" data-mp="r9zeldu458m5" />
        </div>
        <div role="listbox" aria-label={`${groupTitle} catalog`} className="mt-2 max-h-36 overflow-y-auto rounded-lg border border-slate-200 bg-white p-1" data-id="element-73" data-mp="9y0wuylcsc5z">
          {filteredCatalog.length ? filteredCatalog.map(item => {
          const Icon = item.icon;
          const alreadyAdded = normalizedLabels.has(item.label.toLowerCase());
          return <button key={item.label} type="button" role="option" aria-selected={label === item.label} disabled={alreadyAdded} onClick={() => chooseCatalogItem(item)} className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E] ${alreadyAdded ? 'cursor-not-allowed text-slate-400' : label === item.label ? 'bg-[#C8102E]/8 text-[#C8102E]' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-74" data-mp="3n76vd4puyw6t">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-100 text-slate-600" data-id="element-75" data-mp="3miy4izd14jl7">
                    <Icon className="h-4 w-4" data-id="element-76" data-mp="42vph018hall" />
                  </span>
                  <span className="flex-1 font-medium" data-id="element-77" data-mp="222q9zj21ua">{item.label}</span>
                  {alreadyAdded && <span className="text-xs font-semibold" data-id="element-78" data-mp="e3egqfv4ntdj">Already added</span>}
                </button>;
        }) : <p className="px-3 py-4 text-center text-sm text-slate-500" data-id="element-79" data-mp="t5cu83h6c3a1">
              No catalog items match “{searchTerm}”.
            </p>}
        </div>
      </div>

      <p className="mt-3 text-xs text-slate-500" data-id="element-80" data-mp="x4vecx168abt">
        Select an available catalog item to add it to this profile.
      </p>

      <div className="mt-4 flex justify-end gap-2" data-id="element-81" data-mp="16why5hrkrhq6">
        <button type="button" onClick={onCancel} className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E]" data-id="element-82" data-mp="16vztpxurdx76">
          Cancel
        </button>
        <button type="button" disabled={!label.trim() || duplicateLabel} onClick={onAdd} className="inline-flex items-center gap-1.5 rounded-lg bg-[#C8102E] px-3 py-2 text-sm font-semibold text-white hover:bg-[#A50D24] disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E]" data-id="element-83" data-mp="j6fbxiacd5zh">
          <PlusIcon className="h-4 w-4" data-id="element-84" data-mp="3k2678oicp32h" /> Add item
        </button>
      </div>
    </div>;
}
type PreferenceRowProps = {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
};
function PreferenceRow({
  id,
  label,
  description,
  checked,
  onChange
}: PreferenceRowProps) {
  return <label htmlFor={id} className="flex cursor-pointer items-center justify-between gap-4 px-4 py-3" data-id="element-85" data-mp="35cqr02gg4g11">
      <span data-id="element-86" data-mp="2we6jlxgc5ss4">
        <span className="block text-sm font-semibold text-slate-800" data-id="element-87" data-mp="2wdn8p0p3s4nl">
          {label}
        </span>
        <span className="mt-0.5 block text-xs text-slate-500" data-id="element-88" data-mp="2re0no5319k48">
          {description}
        </span>
      </span>
      <span className="relative inline-flex shrink-0 items-center" data-id="element-89" data-mp="2mh0pegcfdlno">
        <input id={id} type="checkbox" checked={checked} onChange={event => onChange(event.target.checked)} className="peer sr-only" data-id="element-90" data-mp="pnyxgaw1u4e2" />
        <span className="h-5 w-9 rounded-full bg-slate-300 transition-colors peer-checked:bg-[#C8102E] peer-focus-visible:ring-2 peer-focus-visible:ring-[#C8102E] peer-focus-visible:ring-offset-2 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:shadow-sm after:transition-transform peer-checked:after:translate-x-4" data-id="element-91" data-mp="2f4udbsxl7vzg" />
      </span>
    </label>;
}